import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  Calendar,
  Tag,
  Maximize2,
  CheckCircle2,
  Award
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RandIcon = ({ size = 20, className = '' }) => (
  <span
    aria-hidden="true"
    className={`inline-flex items-center justify-center font-heading font-medium leading-none ${className}`}
    style={{ width: size, height: size, fontSize: size * 1.1 }}
  >
    R
  </span>
);

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    fetchProject();
    setActiveImage(0);
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-20">
        <div className="text-text-muted">Loading...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="font-heading text-3xl text-text-main mb-4">Project Not Found</h2>
          <Link to="/projects" className="text-primary hover:underline">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  const gallery = project.gallery && project.gallery.length > 0 ? project.gallery : [project.image];
  const hasRichData = project.budget || project.size || project.completion || project.highlights;

  return (
    <main data-testid="project-detail-page" className="bg-background min-h-screen pt-20">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Link
          to="/projects"
          data-testid="back-to-projects"
          className="inline-flex items-center gap-2 text-text-muted hover:text-primary transition-colors font-mono text-xs uppercase tracking-widest"
        >
          <ArrowLeft size={14} />
          Back to Projects
        </Link>
      </div>

      {/* Hero Image / Gallery */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="aspect-[21/9] overflow-hidden"
          >
            <img
              src={gallery[activeImage]}
              alt={project.title}
              className="w-full h-full object-cover transition-all duration-500"
            />
          </motion.div>
          {gallery.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-3">
              {gallery.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImage(idx)}
                  data-testid={`gallery-thumb-${idx}`}
                  className={`aspect-square overflow-hidden border transition-all ${
                    activeImage === idx
                      ? 'border-primary'
                      : 'border-border opacity-60 hover:opacity-100 hover:border-primary/60'
                  }`}
                >
                  <img
                    src={img}
                    alt={`${project.title} ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Project Info */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                {project.featured && (
                  <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary border border-primary/40 bg-primary/5 px-3 py-1">
                    <Award size={12} />
                    Featured
                  </span>
                )}
                <span className="font-mono text-xs uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 capitalize">
                  {project.category}
                </span>
                <span className="font-mono text-xs uppercase tracking-widest text-text-muted">
                  {project.type}
                </span>
              </div>

              <h1 className="font-heading font-light text-4xl md:text-5xl lg:text-6xl text-text-main tracking-tight">
                {project.title}
              </h1>

              <p className="mt-8 text-text-muted font-body text-lg leading-relaxed">
                {project.description}
              </p>

              {project.highlights && project.highlights.length > 0 && (
                <div className="mt-12">
                  <h3 className="font-heading text-2xl text-text-main mb-6 tracking-tight">
                    Project Highlights
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {project.highlights.map((highlight) => (
                      <li
                        key={highlight}
                        className="flex items-center gap-3 text-text-muted text-sm"
                      >
                        <CheckCircle2 size={16} className="text-primary flex-shrink-0" />
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!hasRichData && (
                <div className="mt-12 prose prose-invert max-w-none">
                  <h3 className="font-heading text-2xl text-text-main mb-4">Project Overview</h3>
                  <p className="text-text-muted font-body leading-relaxed">
                    This project showcases our commitment to excellence in{' '}
                    {project.type === 'renovation' ? 'renovation' : 'construction'} work. Every
                    detail was carefully considered to deliver a result that exceeds expectations
                    while meeting the unique needs of our client.
                  </p>
                  <p className="text-text-muted font-body leading-relaxed mt-4">
                    Working closely with the client throughout the process, we ensured that every
                    aspect of the project aligned with their vision. From initial consultation to
                    final handover, our team maintained the highest standards of quality and
                    professionalism.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-1"
            >
              <div className="bg-surface border border-border p-8 sticky top-32">
                <h3 className="font-mono text-xs uppercase tracking-widest text-primary mb-6">
                  Project Details
                </h3>

                <div className="space-y-6">
                  {project.location && (
                    <div className="flex items-start gap-4">
                      <MapPin size={20} className="text-primary flex-shrink-0 mt-1" />
                      <div>
                        <div className="font-mono text-xs uppercase tracking-widest text-text-muted mb-1">
                          Location
                        </div>
                        <div className="text-text-main">{project.location}</div>
                      </div>
                    </div>
                  )}

                  {(project.completion || project.year) && (
                    <div className="flex items-start gap-4">
                      <Calendar size={20} className="text-primary flex-shrink-0 mt-1" />
                      <div>
                        <div className="font-mono text-xs uppercase tracking-widest text-text-muted mb-1">
                          {project.completion ? 'Completion' : 'Year'}
                        </div>
                        <div className="text-text-main">
                          {project.completion || project.year}
                        </div>
                      </div>
                    </div>
                  )}

                  {project.budget && (
                    <div className="flex items-start gap-4">
                      <RandIcon size={20} className="text-primary flex-shrink-0 mt-1" />
                      <div>
                        <div className="font-mono text-xs uppercase tracking-widest text-text-muted mb-1">
                          Budget
                        </div>
                        <div className="text-text-main">{project.budget}</div>
                      </div>
                    </div>
                  )}

                  {project.size && (
                    <div className="flex items-start gap-4">
                      <Maximize2 size={20} className="text-primary flex-shrink-0 mt-1" />
                      <div>
                        <div className="font-mono text-xs uppercase tracking-widest text-text-muted mb-1">
                          Size
                        </div>
                        <div className="text-text-main">{project.size}</div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-4">
                    <Tag size={20} className="text-primary flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-mono text-xs uppercase tracking-widest text-text-muted mb-1">
                        Category
                      </div>
                      <div className="text-text-main capitalize">{project.category}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-border">
                  <Link
                    to="/contact"
                    data-testid="project-detail-cta"
                    className="btn-primary border border-primary text-primary w-full py-4 font-mono text-xs uppercase tracking-widest hover:text-background transition-colors flex items-center justify-center gap-2"
                  >
                    Start Your Project
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-surface border-t border-border">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="overline">Like What You See?</span>
          <h2 className="font-heading font-light text-3xl md:text-4xl text-text-main mt-4 tracking-tight">
            Let&apos;s Create Something<br />
            <span className="text-primary">Together</span>
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/projects"
              className="border border-text-muted/30 text-text-main px-8 py-4 font-mono text-xs uppercase tracking-widest hover:border-text-main transition-colors inline-flex items-center justify-center"
            >
              View More Projects
            </Link>
            <Link
              to="/contact"
              className="btn-primary border border-primary text-primary px-8 py-4 font-mono text-xs uppercase tracking-widest hover:text-background transition-colors inline-flex items-center justify-center gap-2"
            >
              Get a Quote
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
